import React, { useEffect } from 'react';
import { Form, Row, Col, Select, Input, DatePicker, Checkbox, Tag } from 'antd';
import { BankOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useConfig } from '../../../../contexts/ConfigContext';
import { COUNTRY_CODES } from '../../../../constants/countries';
import { getDocumentTypes } from '../../../../constants/documentType';

const { Option } = Select;

export const InvoiceHeader = ({
    documentType,
    cajaActiva,
    aperturaActiva,
    turnoNombre,
    permissions,
    hasKitsInLines
}) => {
    const { country } = useConfig();
    const form = Form.useFormInstance();
    const tipoDocumentoSrI = Form.useWatch("Tipo_Documento_SrI", form);

    useEffect(() => {
        if (country?.code === COUNTRY_CODES.PERU && documentType !== "Credit Memo" && tipoDocumentoSrI) {
            if (tipoDocumentoSrI === "01") { // DNI
                form.setFieldValue("Document_Type", "Boleta");
            } else if (tipoDocumentoSrI === "06") { // RUC
                form.setFieldValue("Document_Type", "Invoice");
            }
        }
    }, [tipoDocumentoSrI, country?.code, documentType, form]);

    const documentTypes = !documentType || documentType === "Invoice"
        ? getDocumentTypes(country?.code).filter(opt => opt.value !== "Credit Memo")
        : getDocumentTypes(country?.code);

    return (
        <>
            <h3>
                Datos de{" "}
                {documentType === "Credit Memo"
                    ? "nota de crédito"
                    : documentType === "Boleta"
                        ? "boleta"
                        : "factura"}
            </h3>
            <Row gutter={16}>
                <Col xs={24} sm={8}>
                    <Form.Item
                        name="Document_Type"
                        label="Tipo de documento"
                        rules={[{ required: true, message: "Seleccione un tipo" }]}
                    >
                        <Select disabled={true}>
                            {documentTypes.map((opt) => (
                                <Option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col xs={24} sm={16}>
                    <Form.Item
                        name="No"
                        label={
                            documentType === "Credit Memo"
                                ? "N° Nota de crédito"
                                : "Nº Factura"
                        }
                        tooltip="Número asignado por Business Central después de sincronizar"
                    >
                        <Input disabled placeholder="Pendiente de sincronización" />
                    </Form.Item>
                </Col>
            </Row>

            {documentType === "Credit Memo" && (
                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            name="Applies_to_Doc_No"
                            label="Nº Factura Corregida"
                            tooltip="Número de la factura a la que se aplica esta nota de crédito"
                            rules={[
                                {
                                    required: true,
                                    message:
                                        "Indique la factura a la que se aplica esta nota de crédito",
                                },
                            ]}
                        >
                            <Input
                                disabled
                                placeholder="Introduzca el número de la factura original"
                            />
                        </Form.Item>
                    </Col>
                </Row>
            )}

            <Row gutter={16}>
                <Col xs={24} sm={12}>
                    <Form.Item
                        name="Posting_Date"
                        label="Fecha de registro"
                        rules={[{ required: true, message: "Seleccione una fecha" }]}
                    >
                        <DatePicker
                            disabled={true}
                            style={{ width: "100%" }}
                            format="DD/MM/YYYY"
                        />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                    <Form.Item
                        name="Document_Date"
                        label="Fecha de documento"
                        rules={[{ required: true, message: "Seleccione una fecha" }]}
                    >
                        <DatePicker
                            disabled={true}
                            style={{ width: "100%" }}
                            format="DD/MM/YYYY"
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col xs={24} sm={12}>
                    {/* Campo oculto para mantener el valor real de la caja */}
                    <Form.Item name="caja" hidden={true} />
                    <Form.Item
                        label="Caja"
                        required={true}
                        tooltip="La caja se toma automáticamente de la apertura de caja activa"
                    >
                        <Input
                            disabled={true}
                            value={cajaActiva?.nombre || aperturaActiva?.Caja || ""}
                            placeholder="No hay caja abierta"
                            addonBefore={<BankOutlined />}
                            suffix={
                                aperturaActiva ? (
                                    <span style={{ color: "#52c41a" }}>Caja activa</span>
                                ) : (
                                    <span style={{ color: "#ff4d4f" }}>Sin apertura</span>
                                )
                            }
                        />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                    {/* Campo oculto para mantener el valor real del turno */}
                    <Form.Item name="turno" hidden={true} />
                    <Form.Item
                        label="Turno"
                        required={true}
                        tooltip="El turno se toma automáticamente de la apertura de caja activa"
                    >
                        <Input
                            disabled={true}
                            value={turnoNombre || aperturaActiva?.Turno || ""}
                            placeholder="No hay turno activo"
                            addonBefore={<ClockCircleOutlined />}
                            suffix={
                                aperturaActiva ? (
                                    <span style={{ color: "#52c41a" }}>Turno activo</span>
                                ) : (
                                    <span style={{ color: "#ff4d4f" }}>Sin apertura</span>
                                )
                            }
                        />
                    </Form.Item>
                </Col>
            </Row>

            {permissions?.canBuyIndividualBooks && <Row gutter={16}>
                <Col span={24}>
                    <Form.Item
                        name="libros_sueltos"
                        valuePropName="checked"
                        tooltip="Se activa automáticamente cuando la factura contiene productos tipo kit. También puede activarse manualmente."
                    >
                        <Checkbox>
                            El cliente realiza la compra de libros sueltos y no kit
                            {hasKitsInLines && (
                                <Tag
                                    color="orange"
                                    style={{ marginLeft: 8 }}
                                    title="Se detectaron productos tipo kit en las líneas de factura"
                                >
                                    Kit detectado
                                </Tag>
                            )}
                        </Checkbox>
                    </Form.Item>
                </Col>
            </Row>}
        </>
    );
};
